import type { ReactNode } from "react";
import { SidebarScrollCue } from "@/features/navigation/components/SidebarScrollCue";
import { useNavScrollCue } from "@/features/navigation/hooks/useNavScrollCue";
import { SidebarContent, useSidebar } from "@/shared/components/ui/sidebar";

type SidebarScrollAreaProps = { children: ReactNode };

/**
 * Wraps the scrollable sidebar nav and overlays the "Voir plus" scroll cue.
 * The cue re-measures via a ResizeObserver on the scroll container and its
 * content, so it reacts to the async backend menu growing the list. It is not
 * rendered on mobile (full-height off-canvas drawer) or in the collapsed icon
 * rail.
 */
function SidebarScrollArea({ children }: SidebarScrollAreaProps) {
  const { isMobile, state } = useSidebar();
  const isIconMode = !isMobile && state === "collapsed";
  const {
    scrollRef, contentRef, canScrollDown, scrollToBottom,
  } = useNavScrollCue();

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <SidebarContent ref={scrollRef} tabIndex={-1} className="custom-scrollbar">
        <div ref={contentRef} className="flex flex-col gap-3">
          {children}
        </div>
      </SidebarContent>
      {!isMobile && !isIconMode && (
        <SidebarScrollCue show={canScrollDown} onClick={scrollToBottom} />
      )}
    </div>
  );
}

SidebarScrollArea.displayName = "SidebarScrollArea";

export { SidebarScrollArea };
